import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import { ReportTags } from "./ReportTags";
import { getReportUploadListApi, toReportUploadListTagId } from "./getReportUploadList";
import type { ReportUploadKind } from "./_models/ReportUpload";

export interface DeleteReportUploadArgs {
  report: ReportUploadKind;
  uploadId: string;
  /** yyMMdd: the day's list to refresh. */
  date: string;
}

/** Removes one uploaded raw export from S3 (DELETE /reports/{report}/uploads/{uploadId}), optimistically dropping it from that day's cached list. */
export const deleteReportUploadApi = api.injectEndpoints({
  endpoints: (builder) => ({
    deleteReportUpload: builder.mutation<{ deletedKey: string }, DeleteReportUploadArgs>({
      queryFn: async (args) => {
        try {
          const res = await axios.delete<{ deletedKey: string }>(
            `${backendBase()}/reports/${args.report}/uploads/${encodeURIComponent(args.uploadId)}`,
            { headers: { Accept: "application/json" } },
          );
          return { data: res.data };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      onQueryStarted: async (args, lifecycle) => {
        const removeFromList = getReportUploadListApi.util.updateQueryData(
          "getReportUploadList",
          { report: args.report, date: args.date },
          (files) => files.filter((file) => file.id !== args.uploadId),
        );
        const patch = lifecycle.dispatch(removeFromList);
        try {
          await lifecycle.queryFulfilled;
        }
        catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, args) => [
        { type: ReportTags.ReportUpload, id: toReportUploadListTagId({ report: args.report, date: args.date }) },
      ],
    }),
  }),
});

export const deleteReportUpload = deleteReportUploadApi.endpoints.deleteReportUpload;
